import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, CardFooter, Col, Row } from 'reactstrap';
import { endOfDay, startOfDay } from 'date-fns';
import { useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import FormDateInput from '../../../components/form/FormDateInput';
import FormSelect from '../../../components/form/FormSelect';
import AdminHeader from '../../../components/header/AdminHeader';
import CallexLoader from '../../../components/CallexLoader';

import ProductReportDetails from '../ProductReport/ProductReportDetails';
import {
  archiveOptionsGet,
  callConnectivityReportGet,
  downloadCallConnectivityReport,
  processOptionGetByFlag
} from '../../../store/slices/tenantSlices/dailyReportSlice';
import {
  formatArchiveOptions,
  formatPayloadDate,
  getDateRangeFromLabel
} from '../../../utils/common';
import { useTimezone } from '../../../store/slices/commonSlice';
import { toZonedTime } from 'date-fns-tz';

const CallConnectivityReport = () => {
  const dispatch = useDispatch();
  const timezone = useTimezone();
  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [processOptions, setProcessOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [reportData, setReportData] = useState({
    normalClearing: [],
    other: []
  });
  const [infoDetails, setInfoDetails] = useState(null);
  const [archiveOptions, setArchiveOptions] = useState([]);

  const today = toZonedTime(new Date(), timezone);

  const initialValues = {
    start_date: startOfDay(today),
    end_date: endOfDay(today),
    processId: '',
    archiveId: 0
  };

  const validationSchema = Yup.object({
    start_date: Yup.date().required('Start date is required'),
    end_date: Yup.date().required('End date is required')
  });

  const onSubmit = async () => {
    setIsLoading(true);
    setIsFilterOpen(false);
    try {
      const formattedStartDate = formatPayloadDate(values.start_date);
      const formattedEndDate = formatPayloadDate(values.end_date);
      const payload = {
        ...values,
        processId: values?.processId || 'All'
      };
      payload['start_date'] = formattedStartDate;
      payload['end_date'] = formattedEndDate;

      const response = await dispatch(callConnectivityReportGet(payload)).unwrap();
      if (response) {
        const normalClearingData = response?.data?.filter(
          (item) => item.hangup_cause === 'NORMAL_CLEARING'
        );
        const otherData = response?.data?.filter((item) => item.hangup_cause !== 'NORMAL_CLEARING');

        setReportData({
          normalClearing: [...normalClearingData],
          other: [...otherData]
        });
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log('error', error);
    }
  };

  const { values, handleChange, handleBlur, setFieldValue, handleSubmit, touched, errors } =
    useFormik({
      initialValues,
      validationSchema,
      onSubmit
    });

  const getProcessOption = async () => {
    try {
      const response = await dispatch(
        processOptionGetByFlag({ flag_type: 'voicebroadcast' })
      ).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: val.id
        }));
        setProcessOptions(options);
      } else {
        setProcessOptions([]);
      }
    } catch (error) {
      setProcessOptions([]);
      console.log('error', error);
    }
  };

  const getArchiveOptions = async () => {
    try {
      const response = await dispatch(archiveOptionsGet()).unwrap();
      if (response && response.data) {
        const options = formatArchiveOptions(response.data, timezone);
        setArchiveOptions(options);
      } else {
        setArchiveOptions([]);
      }
    } catch (error) {
      setArchiveOptions([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    getArchiveOptions();
    onSubmit();
    getProcessOption();
  }, []);

  const onDownload = async (hangupCause) => {
    try {
      const formattedStartDate = formatPayloadDate(values.start_date);
      const formattedEndDate = formatPayloadDate(values.end_date);
      const payload = {
        ...values,
        processId: values?.processId || 'All'
      };
      payload['start_date'] = formattedStartDate;
      payload['end_date'] = formattedEndDate;
      payload['hangupCause'] = hangupCause;

      const response = await dispatch(downloadCallConnectivityReport(payload)).unwrap();

      if (response) {
        const fileName = `report_${formattedStartDate}_to_${formattedEndDate}.xlsx`;
        const url = window.URL.createObjectURL(response);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        link.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  const selectedOption = useMemo(
    () => archiveOptions.find((opt) => opt.value === values.archiveId),
    [values.archiveId, archiveOptions]
  );

  const selectedLabel = selectedOption?.label || null;

  const { minDate, maxDate } = useMemo(() => {
    return getDateRangeFromLabel(selectedLabel, timezone);
  }, [selectedLabel, timezone]);

  useEffect(() => {
    if (minDate && maxDate) {
      setFieldValue('start_date', minDate);
      setFieldValue('end_date', maxDate);
    }
  }, [minDate?.getTime(), maxDate?.getTime()]);

  return (
    <div>
      <AdminHeader
        title="Call Connectivity Report"
        isSearch={true}
        onSearchClick={() => setIsFilterOpen(!isFilterOpen)}
        isRefresh
        onRefreshClick={() => onSubmit()}
      />
      {isFilterOpen && (
        <Card className="mb-3 p-3">
          <form onSubmit={handleSubmit}>
            <Row>
              <Col lg="3" md="3" className="flex-row">
                <FormSelect
                  label="Month"
                  placeholder="Select month"
                  value={values.archiveId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="archiveId"
                  options={archiveOptions}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormDateInput
                  dateFormat="yyyy-MM-dd HH:mm:ss"
                  label="From Date"
                  showTimeSelect={true}
                  minDate={minDate}
                  maxDate={values.end_date || maxDate}
                  value={values.start_date}
                  onChange={(e) => setFieldValue('start_date', e.target.value)}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormDateInput
                  label="To Date"
                  dateFormat="yyyy-MM-dd HH:mm:ss"
                  showTimeSelect={true}
                  minDate={values.start_date || minDate}
                  maxDate={maxDate}
                  value={values.end_date}
                  onChange={(e) => setFieldValue('end_date', e.target.value)}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormSelect
                  label="Process"
                  placeholder="Select Process"
                  value={values.processId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="processId"
                  options={processOptions}
                  touched={touched}
                  errors={errors}
                />
              </Col>
            </Row>
            <CardFooter className="p-0 bg-white text-end">
              <Button color="primary" className="px-4" type="submit">
                Search
              </Button>
            </CardFooter>
          </form>
        </Card>
      )}
      {isLoading ? (
        <Card
          className="p-0 d-flex justify-content-center align-items-center m-0"
          style={{ height: isFilterOpen ? 'calc(100vh - 330px)' : 'calc(100vh - 170px)' }}>
          <CallexLoader />
        </Card>
      ) : reportData?.normalClearing?.length > 0 || reportData?.other?.length > 0 ? (
        <Card className="p-0 position-relative mb-0">
          <div
            className="daily-report-table"
            style={{ height: isFilterOpen ? 'calc(100vh - 330px)' : 'calc(100vh - 170px)' }}>
            {reportData?.normalClearing?.length > 0 ? (
              <table className="report-list-table">
                <thead>
                  <tr className="title">
                    <th colSpan={2}>Connected</th>
                  </tr>
                  <tr>
                    <th>Hangup Cause</th>
                    <th style={{ width: '90px' }} className="text-center">
                      Count
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {reportData?.normalClearing?.map((val, index) => {
                    return (
                      <tr key={index}>
                        <td>{val?.hangup_cause}</td>
                        <td>
                          <div
                            className="text-center text-decoration-underline cursor-pointer"
                            onClick={() => onDownload(val?.hangup_cause)}>
                            {val?.count}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  <tr>
                    <td>
                      <strong>Total</strong>
                    </td>
                    <td>
                      <div
                        className="text-center text-decoration-underline cursor-pointer"
                        onClick={() => onDownload('All')}>
                        {reportData?.normalClearing?.reduce(
                          (acc, val) => acc + (val?.count || 0),
                          0
                        )}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            ) : null}
            {reportData?.other?.length > 0 ? (
              <table className="report-list-table">
                <thead>
                  <tr className="title">
                    <th colSpan={2}>Not Connected</th>
                  </tr>
                  <tr>
                    <th>Hangup Cause</th>
                    <th style={{ width: '90px' }} className="text-center">
                      Count
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {reportData?.other?.map((val, index) => {
                    return (
                      <tr key={index}>
                        <td>{val?.hangup_cause}</td>
                        <td>
                          <div
                            className="text-center text-decoration-underline cursor-pointer"
                            onClick={() => onDownload(val?.hangup_cause)}>
                            {val?.count}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  <tr>
                    <td>
                      <strong>Total</strong>
                    </td>
                    <td>
                      <div
                        className="text-center text-decoration-underline cursor-pointer"
                        onClick={() => onDownload('All')}>
                        {reportData?.other?.reduce((acc, val) => acc + (val?.count || 0), 0)}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            ) : null}
          </div>
        </Card>
      ) : (
        <Card
          className="p-0 d-flex justify-content-center align-items-center no-data m-0"
          style={{ height: isFilterOpen ? 'calc(100vh - 330px)' : 'calc(100vh - 170px)' }}>
          No data available
        </Card>
      )}
      <ProductReportDetails
        isOpen={!!infoDetails}
        onCloseClick={() => setInfoDetails(null)}
        infoDetails={infoDetails}
      />
    </div>
  );
};

export default CallConnectivityReport;
